import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Clock, AlertTriangle, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PaywallStatus } from "@/hooks/usePaywall";

interface TrialBannerProps {
  status: PaywallStatus;
}

export function TrialBanner({ status }: TrialBannerProps) {
  const navigate = useNavigate();

  if (status.reason === "ok" && (status.trialDaysLeft === null || status.trialDaysLeft > 7)) return null;

  const getContent = () => {
    if (status.reason === "suspended") {
      return {
        icon: <XCircle className="h-4 w-4" />,
        variant: "destructive" as const,
        message: "Sua conta está suspensa. Atualize seu pagamento para continuar usando o SYRA.",
        cta: "Atualizar Pagamento",
      };
    }
    if (status.reason === "trial_expired") {
      return {
        icon: <XCircle className="h-4 w-4" />,
        variant: "destructive" as const,
        message: "Seu trial expirou. Escolha um plano para continuar.",
        cta: "Escolher Plano",
      };
    }
    if (status.reason === "limit_reached") {
      return {
        icon: <XCircle className="h-4 w-4" />,
        variant: "destructive" as const,
        message: "Limite de uso atingido. Faça upgrade para continuar.",
        cta: "Fazer Upgrade",
      };
    }
    if (status.billingStatus === "past_due") {
      return {
        icon: <AlertTriangle className="h-4 w-4" />,
        variant: "default" as const,
        message: `Pagamento pendente. Você tem ${status.graceDaysLeft ?? 0} dia(s) restante(s) antes da suspensão.`,
        cta: "Atualizar Pagamento",
      };
    }
    if (status.trialDaysLeft !== null && status.trialDaysLeft <= 7) {
      return {
        icon: <Clock className="h-4 w-4" />,
        variant: "default" as const,
        message: `Seu trial termina em ${status.trialDaysLeft} dia(s). Escolha um plano para não perder acesso.`,
        cta: "Ver Planos",
      };
    }
    return null;
  };

  const content = getContent();
  if (!content) return null;

  return (
    <Alert variant={content.variant} className="mb-4">
      {content.icon}
      <AlertDescription className="flex items-center justify-between gap-4">
        <span>{content.message}</span>
        <Button size="sm" variant={content.variant === "destructive" ? "destructive" : "outline"} onClick={() => navigate("/billing")}>
          {content.cta}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
